'use client';

import { FC } from 'react';
import { ArrowRight, Play, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAnnouncements } from '@/contexts/AnnouncementsProvider';
import type { Announcement } from '@/services/announcements';
import { getAnnouncementTypeConfig } from './typeConfig';

function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days < 7 ? `${days}d ago` : `${Math.floor(days / 7)}w ago`;
}

interface AnnouncementsPanelProps {
  /** Invoked when a CTA needs link/lightbox handling (provided by the bell host). */
  onCta: (a: Announcement) => void;
}

/** "What's new" feed — the panel content rendered inside the bell popover/sheet. */
const AnnouncementsPanel: FC<AnnouncementsPanelProps> = ({ onCta }) => {
  const { panelFeed, markAllRead, unreadCount } = useAnnouncements();

  return (
    <div className="flex max-h-[460px] w-full flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div className="text-[15px] font-semibold text-slate-900">What&apos;s new</div>
        {unreadCount > 0 && (
          <button type="button" onClick={markAllRead} className="rounded-md px-1.5 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-50">
            Mark all read
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {panelFeed.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-400">No announcements yet.</div>
        ) : (
          panelFeed.map((a, i) => {
            const c = getAnnouncementTypeConfig(a.type);
            const Icon = c.icon;
            const bullets = (a.bullets ?? []).filter((b) => b && b.trim());
            const isVideo = a.media_type === 'video';
            return (
              <div
                key={a.id}
                className={cn(
                  'flex gap-3 px-4 py-3.5',
                  i < panelFeed.length - 1 ? 'border-b border-slate-100' : '',
                  a.seen ? 'bg-white' : 'bg-blue-50/40',
                )}
              >
                <div className={cn('mt-0.5 flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-xl border bg-white', c.tintBorder, c.accentText)}>
                  <Icon className="h-[15px] w-[15px]" strokeWidth={2.1} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={cn('text-[10.5px] font-bold uppercase tracking-wider', c.accentText)}>{c.label}</span>
                    {!a.seen && <span className={cn('h-1.5 w-1.5 rounded-full', c.dotBg)} />}
                  </div>
                  <div className="mt-0.5 text-[13.5px] font-semibold text-slate-900">{a.title}</div>
                  {a.body && <div className="mt-0.5 text-[12.5px] leading-relaxed text-slate-600">{a.body}</div>}
                  {bullets.length > 0 && (
                    <ul className="mt-2 flex flex-col gap-1 p-0">
                      {bullets.map((it, j) => (
                        <li key={j} className="flex items-start gap-1.5 text-[12.5px] text-slate-600">
                          <Check className={cn('mt-0.5 h-3 w-3 shrink-0', c.accentText)} strokeWidth={2.6} />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 flex items-center gap-3">
                    {a.cta_label && (
                      <button
                        type="button"
                        onClick={() => onCta(a)}
                        className={cn('inline-flex items-center gap-1 text-[12.5px] font-semibold', c.accentText)}
                      >
                        {isVideo && <Play className="h-3 w-3" strokeWidth={2.4} />}
                        {a.cta_label}
                        {!isVideo && <ArrowRight className="h-3 w-3" strokeWidth={2.4} />}
                      </button>
                    )}
                    <span className="text-[11.5px] text-slate-400">{relativeTime(a.created_at)}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AnnouncementsPanel;
